import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { deleteCookie } from '@/lib/cookies'
import {
  IMPERS_STASH_COOKIE, IMPERS_ACTIVE_TAB_KEY, GOD_MODE_SESSION_KEY,
} from './storage-keys'
import type { ImpersonationAdminStash } from './types'

/**
 * God mode marker: ?tenant=X na URL → seta sessionStorage pro gate.
 * Permite navegação interna no app sem manter o param na URL.
 *
 * Defense-in-depth: god mode por definição NUNCA é impersonação. Limpa resíduo
 * de impersonação + valida que a sessão atual é de platform admin. Cobre 2
 * cenários problemáticos:
 *  (a) aba pai (admin) com bundle cacheado antigo (sem cleanup) — flag
 *      IMPERS_ACTIVE_TAB_KEY vaza pra aba filha via sessionStorage herdado.
 *  (b) aba filha (app) tinha sessão de impersonação prévia em localStorage e
 *      cookie cross-subdomain admin expirou/cresceu além do max-size — a sessão
 *      lida vira a do user impersonado em vez do admin.
 *
 * Em (b), localStorage e cookie estão em estado inconsistente e a aba não pode
 * entrar em god mode confiavelmente. Sai pra /login pra forçar refresh de
 * sessão limpa do admin.
 */
export function useGodModeMarker(
  setImpersStash: (s: ImpersonationAdminStash | null) => void
) {
  useEffect(() => {
    if (typeof window === 'undefined') return
    const params = new URLSearchParams(window.location.search)
    const tenantParam = params.get('tenant')
    if (!tenantParam) return

    deleteCookie(IMPERS_STASH_COOKIE)
    try { sessionStorage.removeItem(IMPERS_ACTIVE_TAB_KEY) } catch { /* ignore */ }
    setImpersStash(null)
    try { sessionStorage.setItem(GOD_MODE_SESSION_KEY, tenantParam) } catch { /* ignore */ }
    params.delete('tenant')
    const newUrl = window.location.pathname +
      (params.toString() ? `?${params.toString()}` : '') +
      window.location.hash
    window.history.replaceState({}, '', newUrl)

    // Valida que a sessão atual é de platform admin. Se não for, é estado
    // corrupto (sessão stale de impersonação anterior) — não é seguro entrar
    // em god mode com o user errado.
    ;(async () => {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.user?.id) return // sem sessão; ProtectedRoute manda pro login
      const { data: paRow } = await supabase
        .from('platform_admins')
        .select('user_id')
        .eq('user_id', session.user.id)
        .maybeSingle()
      if (paRow) return // ok, é admin de verdade
      // Sessão NÃO é admin: limpa tudo e manda pro login pra forçar nova sessão.
      console.warn('[godmode] sessão local não é de platform admin, redirecionando pra login')
      try { sessionStorage.removeItem(GOD_MODE_SESSION_KEY) } catch { /* ignore */ }
      try { localStorage.clear() } catch { /* ignore */ }
      await supabase.auth.signOut().catch(() => {})
      window.location.href = '/login'
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
